/**
 * Format Registry — single source of truth for all book format knowledge.
 *
 * Consumers query this registry via getFormatConfig(fileType) or the capability
 * helpers (isReflowable, isFixed, getMimeType). They must NEVER hardcode format
 * strings like `fileType === 'epub'`.
 *
 * Adding a new format:
 *   1. Add an entry to FORMATS below
 *   2. Implement its parser / metadataExtractor / coverExtractor / languageDetector / preProcessor
 *   3. No other files need to change
 *
 * Each capability slot is a `() => Promise<Module>` that returns an object with
 * a specific method (extract/detect/process/parse). Dynamic imports keep the
 * main bundle lean — format-specific code only loads when actually needed.
 *
 * ─── UnifiedBookData interface ──────────────────────────────────────────────
 *
 * Every format parser's `parse(arrayBuffer)` must return:
 *
 *   {
 *     metadata: { title, author, language, isbn, identifiers? },
 *     spine: Array<{ href, id }>,              // reading order
 *     toc: Array<{ label, href, level }>,
 *     allChaptersHTML: string[],               // processed HTML per spine item
 *     originalChapterHTMLs: string[],          // raw HTML per spine item (for chunk hash)
 *     globalCSS: string,                       // scoped CSS for .epub-content
 *     zip: Object | null,                      // JSZip or VirtualZip for image lookup
 *     opfPath: string | null,                  // EPUB has it; other formats null
 *   }
 */

const FORMATS = {
  epub: {
    renderType: 'reflowable',
    parser:            () => import('../epub/epubBookParser.js'),
    metadataExtractor: () => import('../epub/epubBookParser.js').then(m => ({ extract: m.parseMetadata })),
    coverExtractor:    () => import('../coverExtractor.js').then(m => ({ extract: m.extractEpubCover })),
    languageDetector:  () => import('../api/languageDetect.js').then(m => ({ detect: m.detectEpubLanguage })),
    // Preprocessor is invoked via .call(this, ...) inside the api mixin, so it
    // can use this.matchCanonicalBook / this.updateBook.
    preProcessor:      () => import('../api/bookManager.js').then(m => ({ process: m.preProcessEpubChunks })),
    mimeType: 'application/epub+zip',
    supportsChunks: true,
    supportsSearch: true,
  },
  mobi: {
    renderType: 'reflowable',
    parser:            () => import('../mobi/mobiBookParser.js'),
    metadataExtractor: () => import('../mobi/index.js').then(m => ({ extract: m.parseMobiMetadata })),
    coverExtractor:    () => import('../mobi/index.js').then(m => ({ extract: m.extractMobiCover })),
    languageDetector:  () => import('../mobi/index.js').then(m => ({ detect: m.detectMobiLanguage })),
    preProcessor:      () => import('../mobi/index.js').then(m => ({ process: m.preProcessMobiChunks })),
    mimeType: 'application/x-mobipocket-ebook',
    supportsChunks: true,
    supportsSearch: true,
  },
  pdf: {
    // PDF upload is now accepted — backend pdf-conversion-worker transforms
    // the uploaded PDF into an EPUB (file_type 'epub_from_pdf') and the
    // reader opens the EPUB side. This entry kept for:
    //   1. accept filter: `getAvailableFormats()` returns ['epub','mobi','pdf']
    //      so Library.jsx's upload accept includes '.pdf' again
    //   2. cover extraction: pdfjs-based, runs at upload time so the Library
    //      card has a real cover even before conversion completes (§P3.9 F1)
    //   3. dual-view original mode: Reader still uses isFixed() logic to
    //      route dual-view's "original mode" tab to pdfjs rendering
    // The "renderType: fixed" here does NOT mean users open the uploaded PDF
    // directly. Once upload completes the row flips to file_type='epub_from_pdf'
    // and the book opens through EpubReader. See plans/pdf-reader-upgrade.md.
    renderType: 'fixed',
    parser:            null,
    metadataExtractor: null,
    coverExtractor:    () => import('../coverExtractor.js').then(m => ({ extract: m.extractPdfCover })),
    languageDetector:  null,
    preProcessor:      null,
    mimeType: 'application/pdf',
    supportsChunks: false,
    supportsSearch: false,
    available: true,
  },
  // PDF-converted EPUB — produced server-side by pdf-conversion-worker and
  // delivered to reader via sync. Users never upload this file_type directly;
  // it appears only on books whose file_type was flipped from 'pdf' after
  // conversion. Everything below mirrors the epub entry so the EpubReader
  // pipeline (parse, chunks, cover) works identically.
  epub_from_pdf: {
    renderType: 'reflowable',
    parser:            () => import('../epub/epubBookParser.js'),
    metadataExtractor: () => import('../epub/epubBookParser.js').then(m => ({ extract: m.parseMetadata })),
    coverExtractor:    () => import('../coverExtractor.js').then(m => ({ extract: m.extractEpubCover })),
    languageDetector:  () => import('../api/languageDetect.js').then(m => ({ detect: m.detectEpubLanguage })),
    preProcessor:      null,  // chunks already written server-side via pdf-conversion-worker
    mimeType: 'application/epub+zip',
    supportsChunks: true,
    supportsSearch: true,
    available: true,
  },
  txt: {
    // Not yet implemented — reserved slot.
    renderType: 'reflowable',
    parser:            null,
    metadataExtractor: null,
    coverExtractor:    null,
    languageDetector:  null,
    preProcessor:      null,
    mimeType: 'text/plain',
    supportsChunks: false,
    supportsSearch: false,
  },
};

/**
 * Get the full config entry for a file type. Returns null for unknown formats.
 */
export function getFormatConfig(fileType) {
  if (!fileType) return null;
  return FORMATS[String(fileType).toLowerCase()] || null;
}

/**
 * True if the format reflows text (EPUB-style pipeline: EpubReader + chunks + search).
 */
export function isReflowable(fileType) {
  return getFormatConfig(fileType)?.renderType === 'reflowable';
}

/**
 * True if the format uses fixed layout (PDF-style pipeline: PdfReader).
 */
export function isFixed(fileType) {
  return getFormatConfig(fileType)?.renderType === 'fixed';
}

/**
 * True if the format has a working parser (can be opened in the reader).
 * Reflowable formats without a parser (e.g. future txt) return false.
 */
export function isSupported(fileType) {
  const cfg = getFormatConfig(fileType);
  if (!cfg) return false;
  if (cfg.renderType === 'fixed') return true; // PDF is supported via PdfReader
  return !!cfg.parser;
}

/**
 * Get the MIME type for a file type. Falls back to application/octet-stream.
 */
export function getMimeType(fileType) {
  return getFormatConfig(fileType)?.mimeType || 'application/octet-stream';
}

/**
 * True if the format supports AI chunk annotations.
 */
export function supportsChunks(fileType) {
  return !!getFormatConfig(fileType)?.supportsChunks;
}

/**
 * True if the format supports in-book search.
 */
export function supportsSearch(fileType) {
  return !!getFormatConfig(fileType)?.supportsSearch;
}

/**
 * Get all registered format file type keys (for UI accept filters, tests, etc.).
 */
export function getRegisteredFormats() {
  return Object.keys(FORMATS);
}

/**
 * True if the format is currently available to users (upload + open).
 * A format may be supported technically but temporarily disabled
 * (e.g. a reader component has a known crash). Defaults to true when unset.
 *
 * Dev bypass: `npm run dev` (import.meta.env.DEV === true) ignores the
 * `available` flag so we can keep iterating on disabled formats locally.
 * Vitest runs with MODE === 'test' and is NOT bypassed — tests see prod
 * behavior. Vite inlines import.meta.env.DEV to `false` in prod builds, so
 * this branch is dead code in the shipped bundle.
 */
export function isAvailable(fileType) {
  const cfg = getFormatConfig(fileType);
  if (!cfg) return false;
  try {
    if (import.meta.env?.DEV && import.meta.env?.MODE !== 'test') return true;
  } catch { /* import.meta not available in some environments */ }
  return cfg.available !== false;
}

/**
 * File types users are currently allowed to upload — registered, supported,
 * and not flagged as disabled. Sort order matches FORMATS declaration.
 */
export function getAvailableFormats() {
  return Object.keys(FORMATS).filter(k => isSupported(k) && isAvailable(k));
}
